import { useAvimarkSettings } from "../hooks/useAvimarkSettings";
import type { AvimarkExportSettings } from "@common/lib/api/avimarkEditor";
import "../components/avimarkReplica.css";

const HELP_TICK = "Or: Examined, Normal, for clinics that always do a full exam.";
const HELP_WORDING =
    "Or: the vet's own words, like \"Clear, no discharge\". AVImark shows up to 50 characters per body system. If the vet's wording is longer, your AVImark wording is used instead.";

const SELECT =
    "block w-full max-w-xs rounded-md border-0 py-2 pl-3 pr-10 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-max-700 disabled:bg-gray-50 disabled:text-gray-500";

const Tick = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M1.5 5.5l2.5 2.5L8.5 2" fill="none" stroke="#111" strokeWidth="1.6" />
    </svg>
);

/** A small copy of AVImark's Medical Condition body-system rows (canvas F10), using the editor's replica styles. */
function Example({ s }: { s: AvimarkExportSettings }) {
    const vet = s.normalWording === "vet";
    const allMapped = s.tickRule === "all-mapped";
    const rows: { sys: string; text: string; ticked: boolean; typed: boolean }[] = [
        { sys: "Eyes", text: "Mild conjunctivitis OS", ticked: true, typed: true },
        { sys: "Ears", text: vet ? "Clear, no discharge" : "No debris, TM intact", ticked: true, typed: vet },
        { sys: "Diet", text: allMapped ? "Normal" : "Not Examined", ticked: allMapped, typed: false },
    ];
    const caption = [
        "Eyes has a finding, so AVImark lists it as abnormal.",
        vet
            ? "Ears was examined and normal, so it shows the vet's own words."
            : "Ears was examined and normal, so it shows your AVImark wording.",
        allMapped
            ? "Diet wasn't mentioned, so it's ticked and shows your AVImark wording."
            : "Diet wasn't mentioned, so it's left unticked.",
    ].join(" ");
    return (
        <figure className="aw w-full max-w-[360px] self-start" aria-label="Example in AVImark">
            <figcaption className="aw-tb" style={{ height: 22, fontSize: 11 }}>
                <span>
                    Example in <strong>AVImark</strong>
                </span>
            </figcaption>
            <div className="aw-bd" style={{ margin: "0 3px 3px 3px" }}>
                <div className="aw-abn aw-abn-single" style={{ margin: "0 6px", padding: "6px 8px" }}>
                    {rows.map((r) => (
                        <div key={r.sys} className="aw-ar">
                            <span className="aw-ck" aria-label={r.ticked ? "Ticked" : "Not ticked"}>
                                {r.ticked && <Tick />}
                            </span>
                            <span className="aw-lab" style={{ width: 66 }}>
                                {r.sys}
                            </span>
                            <span className={r.typed ? "aw-af aw-typed" : "aw-af"}>{r.text}</span>
                        </div>
                    ))}
                </div>
                <p className="px-2 pt-1 font-jakarta text-[11px] leading-[15px] text-[#2a1f2b]">{caption}</p>
            </div>
        </figure>
    );
}

/** EN-1249 F10: "How AVImark records the exam". Each change saves at once. */
export default function SettingsBlock() {
    const { settings, save, isSaving, saveError, savedAt, isLoading, isError, refetch } = useAvimarkSettings(true);
    if (isLoading) return <div className="h-24 animate-pulse rounded-md bg-gray-100" />;
    if (isError || !settings) {
        return (
            <p className="text-sm text-gray-700">
                Couldn't load AVImark settings.{" "}
                <button type="button" className="underline" onClick={refetch}>
                    Try again
                </button>
            </p>
        );
    }
    return (
        <section className="flex flex-col gap-3" aria-labelledby="avimark-records">
            <div>
                <h3 id="avimark-records" className="text-base font-semibold text-gray-900">
                    How AVImark records the exam
                </h3>
                <p className="text-sm text-gray-600">
                    How body systems appear on the exam in AVImark. The example updates as you change either setting.
                </p>
            </div>
            <div className="flex flex-wrap items-start gap-6">
                <div className="flex min-w-0 flex-1 basis-80 flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="avimark-tick-rule" className="text-sm font-medium">
                            Body systems the note doesn't mention
                        </label>
                        <select
                            id="avimark-tick-rule"
                            className={SELECT}
                            value={settings.tickRule}
                            disabled={isSaving}
                            onChange={(e) =>
                                save({ ...settings, tickRule: e.target.value as AvimarkExportSettings["tickRule"] })
                            }
                        >
                            <option value="mentioned">Not Examined</option>
                            <option value="all-mapped">Examined, Normal</option>
                        </select>
                        <span className="text-xs text-gray-500">{HELP_TICK}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label htmlFor="avimark-normal-wording" className="text-sm font-medium">
                            Body systems the note says are normal
                        </label>
                        <select
                            id="avimark-normal-wording"
                            className={SELECT}
                            value={settings.normalWording}
                            disabled={isSaving}
                            onChange={(e) =>
                                save({
                                    ...settings,
                                    normalWording: e.target.value as AvimarkExportSettings["normalWording"],
                                })
                            }
                        >
                            <option value="clinic">Your AVImark normal wording</option>
                            <option value="vet">The vet's own words</option>
                        </select>
                        <span className="text-xs text-gray-500">{HELP_WORDING}</span>
                    </div>
                    <p role="status" className="text-xs">
                        {saveError ? (
                            <span className="text-red-700">Couldn't save. Your previous choice is back.</span>
                        ) : savedAt ? (
                            <span className="text-max-900">Saved</span>
                        ) : null}
                    </p>
                </div>
                <Example s={settings} />
            </div>
        </section>
    );
}
